import { useState, useEffect } from "react";
import { ListLayout } from "../../../layouts";
import { DataGrid, IconButton, Tooltip } from "../../../components";
import { cacheApi, formatPrice, responseErros } from "../../../utils";
import { history, notification, ServicoService } from "../../../services";
import {
  EquipamentoServicoDialog,
  FormFiltroAvancado,
  InsumoServicoDialog,
  MaoObraServicoDialog,
  TerceirizadoServicoDialog,
} from "./components";
import { filtrosUrl, PermisoesHelper } from "../../../helpers";
import { useCacheContext } from "../../../contexts/cache.context";

const ListServicos = () => {
  const permissoesHelper = new PermisoesHelper();
  permissoesHelper.validarPermisao("servicos-visualizar");
  const [loading, setLoading] = useState(false);
  const [servicos, setServicos] = useState([]);
  const [rowCount, setRowCount] = useState(0);
  const [openDialogMaosObra, setOpenDialogMaosObra] = useState(false);
  const [openDialogInsumos, setOpenDialogInsumos] = useState(false);
  const [openDialogEquipamentos, setOpenDialogEquipamentos] = useState(false);
  const [openDialogTerceirizados, setOpenDialogTerceirizados] = useState(false);
  const { cacheDto, setCacheDto } = useCacheContext();
  const buttonSelectOpcoes = [
    { permission: true, descricao: "Importação Excel" },
    { permission: true, descricao: "Mão de Obra" },
    { permission: true, descricao: "Insumos" },
    { permission: true, descricao: "Equipamentos" },
    { permission: true, descricao: "Terceirizado" },
  ];

  const colunas = [
    {
      field: "codigo",
      headerName: "Código",
      flex: 100,
    },
    {
      field: "nome",
      headerName: "Nome",
      filtrar: true,
      flex: 750,
    },
    {
      field: "precoServico",
      headerName: "Preço do Serviço",
      flex: 200,
      sortable: false,
      valueGetter: (params) => formatPrice(params.value),
    },
    {
      field: "acoes",
      headerName: "Ações",
      sortable: false,
      flex: 150,
      disableClickEventBubbling: true,
      headerAlign: "center",
      align: "center",
      renderCell: (params) => {
        const servico = params.row;
        let colorBotaoDesativar = servico.ativado ? "#dc3545" : "#198754";

        return (
          <div className="d-flex align-items-center">
            {permissoesHelper.temPermisao("servicos-editar") && (
              <>
                <IconButton onClick={() => onClickEditServico(servico.id)}>
                  <i
                    className="ph-fill ph-pencil"
                    style={{ color: "#494C62", fontSize: 18 }}
                  ></i>
                </IconButton>
                <Tooltip
                  title={servico.ativado ? "Desativar" : "Ativar"}
                  placement="top"
                >
                  <IconButton
                    onClick={() => onClickDesativarServico(servico.id)}
                  >
                    <i
                      className="ph-bold ph-power"
                      style={{
                        color: colorBotaoDesativar,
                        fontSize: 19,
                      }}
                    ></i>
                  </IconButton>
                </Tooltip>
              </>
            )}
            {permissoesHelper.temPermisao("servicos-excluir") && (
              <IconButton onClick={() => onClickDeleteServico(params.row.id)}>
                <i
                  className="ph-fill ph-trash"
                  style={{ color: "#494C62", fontSize: 18 }}
                ></i>
              </IconButton>
            )}
          </div>
        );
      },
    },
  ];
  const servicoService = new ServicoService();

  useEffect(() => {
    const filtros = filtrosUrl.get();
    buscarServicos(filtros);
  }, []);

  const buscarServicos = async function (filter) {
    setLoading(true);
    const result = await servicoService.getAll(filter);
    if (!result.isAxiosError) {
      setServicos(result.data.rows);
      setRowCount(result.data.count);
      filtrosUrl.set(filter);
    } else {
      responseErros(result);
    }
    setLoading(false);
  };

  const onClickCadastrarServico = function (descricao) {
    switch (descricao) {
      case "Importação Excel":
        history.push("/estoque/importacao-servico");
        break;
      case "Mão de Obra":
        setOpenDialogMaosObra(true);
        break;
      case "Insumos":
        setOpenDialogInsumos(true);
        break;
      case "Equipamentos":
        setOpenDialogEquipamentos(true);
        break;
      case "Terceirizado":
        setOpenDialogTerceirizados(true);
        break;
      default:
        history.push("/estoque/servicos/create");
        break;
    }
  };

  const onClickEditServico = function (id) {
    history.push(`/estoque/servicos/${id}`);
  };

  const onClickDesativarServico = async function (id) {
    const servico = servicos.find((item) => item.id === id);
    const retornoAlerta = await notification.confirmacao(
      `${servico?.ativado ? "Desativar" : "Ativar"}!`,
      `Tem certeza que deseja ${
        servico?.ativado ? "desativar" : "ativar"
      } o serviço '${servico?.descricao}'?`,
    );
    if (retornoAlerta.isConfirmed) {
      setLoading(true);
      const result = await servicoService.desativar(id);
      if (!result.isAxiosError) {
        const filtros = filtrosUrl.get();
        buscarServicos(filtros);
        notification.sucessoGenericos(
          `${
            servico?.ativado ? "Serviço desativado" : "Serviço ativado"
          } com sucesso.`,
        );
      } else {
        setLoading(false);
        responseErros(result);
      }
    }
  };

  const onClickDeleteServico = async function (id) {
    const retornoAlerta = await notification.confirmacao(
      "Excluir!",
      `Tem certeza que deseja excluir esse serviço?`,
    );
    if (retornoAlerta.isConfirmed) {
      setLoading(true);
      const result = await servicoService.deletar(id);
      if (!result.isAxiosError) {
        buscarServicos();
        notification.deletadoSucesso();
      } else {
        setLoading(false);
        responseErros(result);
      }
    }
  };

  const handlePesquisaAvancada = async (filter) => {
    const label = filtrosUrl.getUrl(filter);
    setLoading(true);
    const porIntervalo = [];
    const filtrosAvancadosTemp = {
      ...filter,
    };
    if (
      filtrosAvancadosTemp.precoVendaStart &&
      filtrosAvancadosTemp.precoVendaEnd
    ) {
      porIntervalo.push({
        coluna: "precoVenda",
        de: filtrosAvancadosTemp.precoVendaStart,
        ate: filtrosAvancadosTemp.precoVendaEnd,
      });
    }

    if (
      (filtrosAvancadosTemp.precoVendaStart &&
        !filtrosAvancadosTemp.precoVendaEnd) ||
      (!filtrosAvancadosTemp.precoVendaStart &&
        filtrosAvancadosTemp.precoVendaEnd)
    ) {
      return notification.erroValidacao(
        "Preencher preço de venda inicial e final",
      );
    }

    delete filtrosAvancadosTemp.precoVendaStart;
    delete filtrosAvancadosTemp.precoVendaEnd;
    const cacheBody = {
      dto: { cacheDto, setCacheDto },
      apiFunction: servicoService.getAllFiltroAvancado({
        ...filtrosAvancadosTemp,
        restritiva: true,
        porIntervalo,
      }),
    };
    const result = await cacheApi(label, cacheBody);
    if (!result.isAxiosError) {
      setServicos(result.data.rows);
      setRowCount(result.data.count);
      filtrosUrl.set(label);
    } else {
      responseErros(result);
    }
    setLoading(false);
  };

  const sendServerDatagrid = (props) => {
    const filtros = filtrosUrl.get();
    const filtrosTemp = {
      ...filtros,
      ...props.filtros,
    };
    if (props.tipoFiltro == "simples") {
      buscarServicos(filtrosTemp);
    } else {
      handlePesquisaAvancada(filtrosTemp);
    }
  };

  return (
    <ListLayout
      title="Serviços"
      loading={loading}
      buttonSelectOpcoes={buttonSelectOpcoes}
      onClickCadastrar={onClickCadastrarServico}
      permissaoButton={permissoesHelper.temPermisao("servicos-cadastrar")}
    >
      <DataGrid
        rows={servicos}
        rowCount={rowCount}
        columns={colunas}
        disableAcoes
        enableRowDoubleClick
        onClickEdit={onClickEditServico}
        visualizarShow={permissoesHelper.temPermisao("servicos-visualizar")}
        paginationMode={"server"}
        sendServer={(props) => sendServerDatagrid(props)}
        FormFiltroAvancado={FormFiltroAvancado}
      />
      <MaoObraServicoDialog
        open={openDialogMaosObra}
        setOpen={setOpenDialogMaosObra}
      />
      <InsumoServicoDialog
        open={openDialogInsumos}
        setOpen={setOpenDialogInsumos}
      />
      <EquipamentoServicoDialog
        open={openDialogEquipamentos}
        setOpen={setOpenDialogEquipamentos}
      />
      <TerceirizadoServicoDialog
        open={openDialogTerceirizados}
        setOpen={setOpenDialogTerceirizados}
      />
    </ListLayout>
  );
};

export default ListServicos;
